import {
	DateString,
	Highlight,
	HighlightedText,
	Icon,
	ListItem,
	Space,
	PipeSeparator,
	Typography
} from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import styles from "./KnowledgeItemListItem.module.less";
import { ReactElement } from "react";
import { HubThumbnail } from "../../HubThumbnail/HubThumbnail";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	isNotNil,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemMediaPreviewViewModel } from "client/Api/Hub/common";
import { useGlobalSearchItemActions } from "client/Features/Layout/TitleBar/GlobalSearch/GlobalSearchItem/useGlobalSearchItemActions";

export type KnowledgeItemHighlightableFields =
	| "title";

export type KnowledgeItemListItemProps = {
	title: string;
	categoryName: string | nil;
	authorName: string | nil;
	lastModifiedDate: string | Date;
	media: HubItemMediaPreviewViewModel;
	trackingId: string;
	highlights?: Record<KnowledgeItemHighlightableFields, Highlight | undefined>;
	timezone: TimeZone;
	onClick?: () => void;
	onRemove?: () => void;
	itemIndex?: number;
	tabIndex?: number;
	onExecute?: () => void;
};

export const KnowledgeItemListItem = (props: KnowledgeItemListItemProps): ReactElement => {
	const {

		title,
		categoryName,
		authorName,
		lastModifiedDate,
		media,
		trackingId,
		highlights,
		timezone,
		onClick,
		onRemove,
		itemIndex,
		tabIndex,
		onExecute
	} = props;

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex: itemIndex ?? -1,
		onExecute: onExecute ?? (() => {/** Nope */})
	});

	return (
		<ListItem
			trackingId={trackingId}
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<div className={styles.thumbnailWrapper}>
						<HubThumbnail
							title={title}
							media={media}
							hubItemKind={HubItemKind.KnowledgeItem}
							mediaPlaceholderSize="extra-small"
						/>
					</div>
				}
				onClick={onClick}
			>
				<ListItem.Title>
					<Typography
						weight="bold"
						trackingId="title"
					>
						<HighlightedText
							text={title}
							highlight={highlights?.title}
						/>
					</Typography>
				</ListItem.Title>
				<ListItem.SubTitle>
					<span
						{...setTrackingId("timestamp")}
					>
						<Icon value="pen"/>
						<Space />
						<DateString
							value={lastModifiedDate}
							format="RelativeTimestamp"
							timezone={timezone}
						/>
					</span>
					{isNotNilOrWhiteSpace(categoryName) && (
						<>
							<PipeSeparator />
							<span {...setTrackingId("categoryName")}>
								{categoryName}
							</span>
						</>
					)}
					{isNotNilOrWhiteSpace(authorName) && (
						<>
							<PipeSeparator />
							<span {...setTrackingId("authorName")}>
								{`${localize("_(Edited)By")} ${authorName}`}
							</span>
						</>
					)}
				</ListItem.SubTitle>
			</ListItem.Content>
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
KnowledgeItemListItem.displayName = "KnowledgeItemListItem";